import { FieldArray, Form, Formik } from 'formik'
import { MdAdd, MdContentCopy, MdInfoOutline } from 'react-icons/md'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IABTestConfiguration, ICreateABTestArbitrationFormValues } from '~/types/arbitration'

import VariationCard from '../../components/VariationCard'

import Button from '~/components/Buttons/Button'
import Label from '~/components/Label'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { displayToast } from '~/utils/toast'
import { YupOption } from '~/utils/yup'

import './styles.scss'

interface Props {
  configuration: IABTestConfiguration
  modalId: string
  navigationGraphOptions: IOption[]
  onClose: VoidFunction
  onSubmit: (values: ICreateABTestArbitrationFormValues) => Promise<void>
}

const variationSchema = Yup.object({
  id: YupOption.default(undefined).required(FORM_ERRORS.REQUIRED),
  trafficShare: Yup.number(),
})

const validationSchema = Yup.object({
  controlVariation: variationSchema.required(FORM_ERRORS.REQUIRED),
  placement: YupOption.required(FORM_ERRORS.REQUIRED),
  testedVariations: Yup.array().of(variationSchema),
})

const totalTrafficShare = (values: ICreateABTestArbitrationFormValues): number => {
  const { controlVariation, testedVariations } = values

  const total =
    (controlVariation?.trafficShare || 0) +
    (testedVariations?.map(({ trafficShare }) => trafficShare).reduce((partialSum, a) => partialSum + a, 0) || 0)

  return total
}

const EditModalContent = ({
  configuration,
  modalId,
  navigationGraphOptions,
  onClose,
  onSubmit,
}: Props): ReactElement => {
  const initialValues: ICreateABTestArbitrationFormValues = {
    controlVariation: configuration.composition.control_variation
      ? {
          id: navigationGraphOptions.find(({ value }) => value === configuration.composition.control_variation?.id),
          trafficShare: configuration.composition.control_variation.traffic_share,
        }
      : {
          id: undefined,
          trafficShare: 100,
        },
    placement: { label: configuration.placement, value: configuration.placement },
    testedVariations:
      configuration.composition.tested_variations?.map(({ id, traffic_share }) => ({
        id: navigationGraphOptions.find(({ value }) => value === id),
        trafficShare: traffic_share,
      })) || [],
  }

  const handlePlacementCopy = (value?: string) => {
    if (value) {
      navigator.clipboard.writeText(value)
      displayToast('Placement ID copied to clipboard', 'success')
    }
  }

  return (
    <div className="edit-pool-modal-content">
      <ModalHeader>Edit variations</ModalHeader>
      <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
        {({ values }) => (
          <FieldArray
            name="testedVariations"
            render={({ push, remove }) => (
              <Form>
                <ModalBody>
                  <div className="edit-pool-modal-content__actions">
                    <div className="edit-pool-modal-content__actions__placement">
                      <Label className="placement-label" label={values.placement.value} size="large" />
                      <Button ghost onClick={() => handlePlacementCopy(values.placement.value)} type="button">
                        <MdContentCopy size={14} />
                      </Button>
                    </div>
                    <Button onClick={() => push({ id: undefined, trafficShare: 0 })} type="button">
                      <MdAdd color="#fff" size={16} />
                      Add variation
                    </Button>
                  </div>
                  <div className="edit-pool-modal-content__variations">
                    <div className="edit-pool-modal-content__variations__row">
                      <VariationCard
                        modalId={modalId}
                        name="controlVariation"
                        navigationGraphOptions={navigationGraphOptions}
                        variationType="control"
                      />
                    </div>
                    {values.testedVariations?.map((_, index) => (
                      <div className="edit-pool-modal-content__variations__row" key={index}>
                        <VariationCard
                          modalId={modalId}
                          name={`testedVariations[${index}]`}
                          navigationGraphOptions={navigationGraphOptions}
                          onRemove={() => remove(index)}
                          variationType="variation"
                        />
                      </div>
                    ))}
                  </div>
                </ModalBody>
                <ModalFooter>
                  <div className="footer-container">
                    {totalTrafficShare(values) !== 100 && (
                      <div className="footer-container__error">
                        <MdInfoOutline color="#ca3423" size={16} />
                        Sum of traffic shares must equal 100%. Current is {totalTrafficShare(values)}%.
                      </div>
                    )}
                    <div className="footer-container__buttons">
                      <Button ghost onClick={onClose} type="button">
                        Cancel
                      </Button>
                      <Button type="submit">Save</Button>
                    </div>
                  </div>
                </ModalFooter>
              </Form>
            )}
          />
        )}
      </Formik>
    </div>
  )
}

export default EditModalContent
